/**
 * Centralized Realtime Connection Manager for VA Sprouts
 * Manages all WebSocket connections to prevent conflicts and optimize performance
 * 
 * Features:
 * - Singleton pattern with proper initialization
 * - Connection pooling and health monitoring
 * - Automatic reconnection with exponential backoff
 * - Channel lifecycle management
 * - Connection state persistence
 */
import { supabase } from './supabase';
import { realtimeLogger } from './logger';

/** How often (ms) to poll connection health. Override via constructor option `healthCheckIntervalMs`. */
const DEFAULT_HEALTH_CHECK_INTERVAL_MS = 30000;

class RealtimeConnectionManager {
  constructor({ healthCheckIntervalMs = DEFAULT_HEALTH_CHECK_INTERVAL_MS } = {}) {
    // Singleton check
    if (RealtimeConnectionManager.instance) {
      return RealtimeConnectionManager.instance;
    }
    
    // Connection state
    this.connections = new Map();
    this.connectionStatus = 'disconnected';
    this.isInitializing = false;
    this.lastConnectionAttempt = 0;
    
    // Configuration
    this.maxRetries = 3;
    this.retryDelay = 2000;
    this.healthCheckIntervalMs = healthCheckIntervalMs;
    this.healthCheckInterval = null;
    this.reconnectTimeout = null;
    
    // Statistics
    this.stats = {
      totalConnections: 0,
      failedConnections: 0,
      reconnections: 0,
      lastSuccessfulConnection: null,
      lastFailedConnection: null
    };
    
    // Promise for initialization
    this.connectionPromise = null;
    
    // Set singleton instance
    RealtimeConnectionManager.instance = this;
    
    // Start health monitoring
    this.startHealthMonitoring();

    // Pause health checks when the tab is hidden, resume when visible again
    if (typeof document !== 'undefined') {
      this._visibilityHandler = () => {
        if (document.hidden) {
          this.stopHealthMonitoring();
        } else {
          this.startHealthMonitoring();
          // Catch up immediately on tab focus
          this._performHealthCheck();
        }
      };
      document.addEventListener('visibilitychange', this._visibilityHandler);
    }
  }

  /**
   * Get singleton instance
   */
  static getInstance() {
    if (!RealtimeConnectionManager.instance) {
      RealtimeConnectionManager.instance = new RealtimeConnectionManager();
    }
    return RealtimeConnectionManager.instance;
  }

  /**
   * Initialize the realtime connection with enhanced error handling
   */
  async initialize() {
    // Prevent multiple simultaneous initializations
    if (this.connectionStatus === 'connected') {
      return Promise.resolve();
    }
    
    if (this.isInitializing && this.connectionPromise) {
      return this.connectionPromise;
    }

    this.isInitializing = true;
    this.lastConnectionAttempt = Date.now();
    
    this.connectionPromise = this._performInitialization();
    
    try {
      await this.connectionPromise;
      this.connectionStatus = 'connected';
      this.stats.lastSuccessfulConnection = new Date();
      this.stats.totalConnections++;
      realtimeLogger.debug('✅ RealtimeConnectionManager initialized successfully');
      
    } catch (error) {
      this.connectionStatus = 'error';
      this.stats.lastFailedConnection = new Date();
      this.stats.failedConnections++;
      realtimeLogger.error('❌ RealtimeConnectionManager initialization failed', error);
      throw error;
      
    } finally {
      this.isInitializing = false;
    }

    return this.connectionPromise;
  }

  /**
   * Internal initialization with comprehensive retry logic
   */
  async _performInitialization() {
    let lastError;
    
    for (let attempt = 1; attempt <= this.maxRetries; attempt++) {
      try {
        // Test connection with a simple channel
        await this._testConnection();
        
        return;
        
      } catch (error) {
        lastError = error;
        console.error(`Connection attempt ${attempt} failed:`, error.message);
        
        if (attempt < this.maxRetries) {
          const delay = this.retryDelay * Math.pow(2, attempt - 1);
          await this._delay(delay);
        }
      }
    }
    
    throw new Error(`Failed to establish realtime connection after ${this.maxRetries} attempts. Last error: ${lastError?.message}`);
  }

  /**
   * Test connection with timeout and proper cleanup
   */
  async _testConnection() {
    const testChannelName = `connection-test-${Date.now()}`;
    let testChannel;
    
    try {
      testChannel = supabase.channel(testChannelName);
      
      const connectionTest = new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Connection test timeout (10 seconds)'));
        }, 10000);

        const cleanup = () => {
          clearTimeout(timeout);
          if (testChannel) {
            try {
              testChannel.unsubscribe();
            } catch {
              // Silently handle cleanup errors
            }
          }
        };

        testChannel.subscribe((status, error) => {
          if (error) {
            cleanup();
            reject(error);
            return;
          }

          if (status === 'SUBSCRIBED') {
            cleanup();
            resolve();
          } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            cleanup();
            reject(new Error(`Connection test failed with status: ${status}`));
          }
        });
      });

      await connectionTest;
      
    } catch (error) {
      // Ensure cleanup even if test fails
      if (testChannel) {
        try {
          testChannel.unsubscribe();
        } catch {
          // Ignore cleanup errors
        }
      }
      throw error;
    }
  }

  /**
   * Create a managed channel with enhanced error handling
   */
  createChannel(channelName, config = {}) {
    try {
      // Validate channel name
      if (!channelName || typeof channelName !== 'string') {
        throw new Error('Channel name must be a non-empty string');
      }

      // Check for existing channel
      if (this.connections.has(channelName)) {
        return this.connections.get(channelName).channel;
      }

      // Create channel with enhanced configuration
      const enhancedConfig = {
        ...config,
        // Add any default configurations here
      };

      const channel = supabase.channel(channelName, enhancedConfig);
      
      // Store channel with metadata
      this.connections.set(channelName, {
        channel,
        config: enhancedConfig,
        createdAt: new Date(),
        subscriptionCount: 0,
        status: 'created'
      });
      
      return channel;
      
    } catch (error) {
      console.error(`Failed to create channel '${channelName}':`, error);
      throw error;
    }
  }

  /**
   * Subscribe to a channel with comprehensive error handling
   */
  subscribe(channelName, callback) {
    const connection = this.connections.get(channelName);
    if (!connection) {
      throw new Error(`Channel '${channelName}' not found. Create it first using createChannel()`);
    }

    const { channel } = connection;
    
    try {
      const subscription = channel.subscribe((status, error) => {
        // Update connection metadata
        connection.status = status;
        connection.lastStatusChange = new Date();
        
        // Handle errors
        if (error) {
          console.error(`Channel '${channelName}' subscription error:`, error);
          this._handleChannelError(channelName, error);
        }
        
        // Call user callback
        if (callback) {
          try {
            callback(status, error);
          } catch (callbackError) {
            console.error(`Error in callback for channel '${channelName}':`, callbackError);
          }
        }
      });

      return subscription;
      
    } catch (error) {
      console.error(`Failed to subscribe to channel '${channelName}':`, error);
      throw error;
    }
  }

  /**
   * Handle channel-specific errors
   */
  _handleChannelError(channelName, error) {
    const connection = this.connections.get(channelName);
    if (connection) {
      connection.lastError = {
        error: error.message,
        timestamp: new Date()
      };
    }
  }

  /**
   * Remove a channel with proper cleanup
   */
  removeChannel(channelName) {
    const connection = this.connections.get(channelName);
    if (!connection) {
      return;
    }

    try {
      const { channel } = connection;
      channel.unsubscribe();
      this.connections.delete(channelName);
      
    } catch (error) {
      console.error(`Error removing channel '${channelName}':`, error);
      // Still remove from our tracking even if unsubscribe fails
      this.connections.delete(channelName);
    }
  }

  /**
   * Start health monitoring
   */
  startHealthMonitoring() {
    if (this.healthCheckInterval) {
      return; // Already monitoring
    }

    this.healthCheckInterval = setInterval(() => {
      this._performHealthCheck();
    }, this.healthCheckIntervalMs);
  }

  /**
   * Stop health monitoring
   */
  stopHealthMonitoring() {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = null;
    }
  }

  /**
   * Perform health check
   */
  _performHealthCheck() {
    const channelCount = this.connections.size;
    const unhealthyChannels = [];
    
    // Check each channel status
    for (const [name, connection] of this.connections) {
      if (connection.status === 'CHANNEL_ERROR' || connection.status === 'TIMED_OUT') {
        unhealthyChannels.push(name);
      }
    }
    
    // Check overall connection status
    if (this.connectionStatus !== 'connected' && channelCount > 0) {
      this._scheduleReconnection();
    }
  }

  /**
   * Schedule reconnection with rate limiting
   */
  _scheduleReconnection() {
    // Prevent too frequent reconnection attempts
    const timeSinceLastAttempt = Date.now() - this.lastConnectionAttempt;
    if (timeSinceLastAttempt < 30000) { // 30 seconds minimum
      return;
    }

    if (this.reconnectTimeout) {
      return; // Already scheduled
    }

    this.reconnectTimeout = setTimeout(() => {
      this.reconnectTimeout = null;
      this.reconnect().catch(error => {
        console.error('Scheduled reconnection failed:', error);
      });
    }, 5000); // Wait 5 seconds before reconnecting
  }

  /**
   * Get detailed connection status and statistics
   */
  getStatus() {
    const channelStats = {};
    let healthyChannels = 0;
    
    for (const [name, connection] of this.connections) {
      channelStats[name] = {
        status: connection.status,
        createdAt: connection.createdAt,
        subscriptionCount: connection.subscriptionCount,
        lastStatusChange: connection.lastStatusChange,
        lastError: connection.lastError
      };
      
      if (connection.status === 'SUBSCRIBED') {
        healthyChannels++;
      }
    }
    
    return {
      status: this.connectionStatus,
      isInitializing: this.isInitializing,
      activeChannels: this.connections.size,
      healthyChannels,
      channelNames: Array.from(this.connections.keys()),
      stats: {
        ...this.stats,
        uptime: this.stats.lastSuccessfulConnection ? 
          Date.now() - this.stats.lastSuccessfulConnection.getTime() : 0
      },
      channels: channelStats
    };
  }

  /**
   * Reconnect all channels after connection loss
   */
  async reconnect() {
    try {
      // Store current channel information
      const channelInfo = Array.from(this.connections.entries()).map(([name, connection]) => ({
        name,
        config: connection.config
      }));
      
      // Clean up existing connections
      await this.cleanup();
      
      // Re-initialize connection
      await this.initialize();
      
      // Recreate channels (subscribers need to re-subscribe)
      for (const { name, config } of channelInfo) {
        try {
          this.createChannel(name, config);
        } catch (error) {
          console.error(`Failed to recreate channel '${name}':`, error);
        }
      }
      
      this.stats.reconnections++;
      
    } catch (error) {
      console.error('Failed to reconnect:', error);
      throw error;
    }
  }

  /**
   * Clean up all connections and resources
   */
  async cleanup() {
    // Stop health monitoring
    this.stopHealthMonitoring();

    // Remove page visibility listener
    if (typeof document !== 'undefined' && this._visibilityHandler) {
      document.removeEventListener('visibilitychange', this._visibilityHandler);
      this._visibilityHandler = null;
    }
    
    // Clear reconnection timeout
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    
    // Clean up all channels
    const channelNames = Array.from(this.connections.keys());
    for (const channelName of channelNames) {
      try {
        this.removeChannel(channelName);
      } catch (error) {
        console.error(`Error cleaning up channel '${channelName}':`, error);
      }
    }
    
    // Reset state
    this.connections.clear();
    this.connectionStatus = 'disconnected';
    this.isInitializing = false;
    this.connectionPromise = null;
  }

  /**
   * Utility: Delay function
   */
  _delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// Create and export singleton instance
const realtimeConnectionManager = RealtimeConnectionManager.getInstance();

export default realtimeConnectionManager;
